import React from 'react';
import { useAsset } from '../hooks/useAsset';
import { fetchHeros } from '../services/okMainApi';
import { getHeroDescription } from '../data/heroData';
import { getApiBase } from '../services/apiBase';
import { t, Language } from '../data/translations';
import { useMobileAutoScrollHint } from '../hooks/useMobileAutoScrollHint';

interface AuthScreenProps {
    savedAuth: { username: string; password?: string; token?: string; opalstoneId?: string } | null;
    onDone: (auth: { username: string; token?: string; opalstoneId?: string }) => void;
    language: Language;
}

const AuthScreen: React.FC<AuthScreenProps> = ({ savedAuth, onDone, language }) => {
    const rootRef = React.useRef<HTMLDivElement>(null)
    const [mode, setMode] = React.useState<'login' | 'register'>('login')
    const [step, setStep] = React.useState<'auth' | 'faction'>('auth')
    const [username, setUsername] = React.useState('')
    const [password, setPassword] = React.useState('')
    const [opal, setOpal] = React.useState('')
    const [error, setError] = React.useState<string | null>(null)
    const [selectedFaction, setSelectedFaction] = React.useState<number>(0)
    const [confirmedFaction, setConfirmedFaction] = React.useState<number>(0)
    const [isSubmitting, setIsSubmitting] = React.useState(false)
    const submitLockRef = React.useRef(false)
    const isMountedRef = React.useRef(true)

    React.useEffect(() => {
        return () => {
            isMountedRef.current = false
        }
    }, [])

    useMobileAutoScrollHint({ targetRef: rootRef, sceneKey: `auth-${mode}-${step}` })

    // Updated paths for component location (../assets instead of ./assets)
    const veriskAvatar = new URL('../assets/custom/VeriskAvatar.png', import.meta.url).href
    const erokAvatar = new URL('../assets/custom/Erok-BuruAvatar.png', import.meta.url).href
    const astridAvatar = new URL('../assets/custom/AstridAvatar.png', import.meta.url).href
    const ferrimAvatar = new URL('../assets/custom/FerrimAvatar.png', import.meta.url).href

    const terrikAvatar = new URL('../assets/custom/TerrikAvatar.png', import.meta.url).href
    const terrikCard = new URL('../assets/custom/Terrik.png', import.meta.url).href

    const corilAvatar = new URL('../assets/custom/CorilAvatar.png', import.meta.url).href
    const corilCard = new URL('../assets/custom/Coril.png', import.meta.url).href

    const harmonizedCrest = new URL('../assets/custom/Harmonized.png', import.meta.url).href
    const chaoticCrest = new URL('../assets/custom/Chaootic.png', import.meta.url).href
    const untouchedCrest = new URL('../assets/custom/Untouched.png', import.meta.url).href

    const veriskCard = new URL('../assets/custom/Verisk.png', import.meta.url).href
    const erokCard = new URL('../assets/custom/Erok-Buru.png', import.meta.url).href
    const astridCard = new URL('../assets/custom/Astrid.png', import.meta.url).href
    const ferrimCard = new URL('../assets/custom/Ferrim.png', import.meta.url).href
    const norimAvatar = new URL('../assets/custom/NorimAvatar.png', import.meta.url).href
    const norimCard = new URL('../assets/custom/Norim.png', import.meta.url).href
    const evelionAvatar = new URL('../assets/custom/EvelionAvatar.png', import.meta.url).href
    const evelionCard = new URL('../assets/custom/Evelion.png', import.meta.url).href

    const triffleAsset = new URL('../assets/custom/Trifle.png', import.meta.url).href

    const canShowOpalOnLogin = mode === 'login' && !savedAuth?.opalstoneId
    const showOpal = mode === 'register' || canShowOpalOnLogin

    const [hoveredFaction, setHoveredFaction] = React.useState<number | null>(null)
    const [hoveredHero, setHoveredHero] = React.useState<{ name: string, img: string, card: string } | null>(null)
    const [isOpalFocused, setIsOpalFocused] = React.useState(false)

    const validate = () => {
        if ((username || '').trim().length < 2) { setError(t('error.nameLength', language)); return false }
        if ((password || '').length < 6) { setError(t('error.passwordLength', language)); return false }
        const normalizedOpal = opal.trim()
        if (showOpal && normalizedOpal) {
            const ok = /^[0-9]{8}$/.test(normalizedOpal)
            if (!ok) { setError(t('error.opalFormat', language)); return false }
        }
        if (mode === 'register' && !confirmedFaction) { setError(t('error.chooseFaction', language)); return false }
        setError(null)
        return true
    }

    const handleSubmit = async () => {
        if (!validate()) return
        if (submitLockRef.current) return
        submitLockRef.current = true
        setIsSubmitting(true)
        const base = getApiBase()
        const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
        const postJson = async (path: string, body: any) => {
            let lastError: unknown = null
            for (let attempt = 0; attempt < 2; attempt++) {
                try {
                    const resp = await fetch(`${base}${path}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(body),
                    })
                    const data = await resp.json()
                    return { resp, data }
                } catch (e) {
                    lastError = e
                    if (attempt === 0) await sleep(450)
                }
            }
            throw lastError || new Error('NETWORK_ERROR')
        }
        try {
            let data: any;
            if (mode === 'login') {
                const result = await postJson('/api/login', { username, password, opalstoneId: showOpal ? opal.trim() : undefined })
                data = result.data
                console.log('[Auth] Login response:', result.resp.status, data)
                if (!result.resp.ok || !data.ok) { setError(t('error.loginFailed', language)); return }

                // CRITICAL FIX: Save factionId from server response
                if (data.user && data.user.faction) {
                    const factionMap: Record<string, number> = { 'HARMONIZED': 1, 'CHAOTIC': 2, 'UNTOUCHED': 3 };
                    const factionId = factionMap[data.user.faction];
                    if (factionId) {
                        try {
                            localStorage.setItem('factionId', String(factionId));
                            console.log('[Auth] Saved factionId on LOGIN:', factionId, 'from faction:', data.user.faction);
                        } catch (e) {
                            console.error('[Auth] Failed to save factionId:', e);
                        }
                    }
                }
            } else {
                const factionMap: Record<number, string> = { 1: 'HARMONIZED', 2: 'CHAOTIC', 3: 'UNTOUCHED' }
                const faction = factionMap[confirmedFaction]
                const result = await postJson('/api/register', { username, password, opalstoneId: opal.trim() || undefined, faction })
                data = result.data
                console.log('[Auth] Register response:', result.resp.status, data)
                if (!result.resp.ok || !data.ok) {
                    setError(t('error.registrationFailed', language));
                    return
                }
                try { localStorage.setItem('factionId', String(confirmedFaction)) } catch { }
            }

            if (mode === 'register' && data.warning) {
                console.warn('[Auth] Opalstone warning:', data.warning)
            }

            onDone({ username: data.user.username, token: data.token, opalstoneId: data.user.opalstoneId })

            // Process heroes from response in the background so entry into the game is not blocked.
            void (async () => {
            if (data.user && data.user.heroes) {
                console.log('[Auth] Server returned heroes:', data.user.heroes);
                try {
                    const allHeroes = await fetchHeros().catch(e => {
                        console.error('[Auth] fetchHeros failed:', e);
                        return [];
                    })
                    console.log('[Auth] Fetched allHeroes:', allHeroes.length, allHeroes);
                    const serverHeroes: string[] = data.user.heroes; // e.g. ['VERISK', 'ASTRID']
                    const capturedIds: number[] = [];

                    if (Array.isArray(allHeroes)) {
                        serverHeroes.forEach(serverHeroName => {
                            // Map server name (VERISK) to client ID
                            const match = allHeroes.find((h: any) => {
                                const hName = (h.name || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
                                const sName = serverHeroName.toUpperCase().replace(/[^A-Z0-9]/g, '');
                                return hName === sName;
                            });
                            if (match) {
                                console.log('[Auth] Match found:', serverHeroName, '->', match.id);
                                capturedIds.push(match.id);
                            } else {
                                console.warn('[Auth] No match found for server hero:', serverHeroName);
                            }
                        });

                        // FORCE ADD EVELION IF CHAOTIC
                        if (mode === 'register' && confirmedFaction === 2) {
                            const hero = allHeroes.find((h: any) => h.name.toUpperCase().includes('EVELION'));
                            if (hero && !capturedIds.includes(hero.id)) {
                                capturedIds.push(hero.id);
                            }
                            // FORCE ADD CORIL IF CHAOTIC
                            const coril = allHeroes.find((h: any) => h.name.toUpperCase().includes('COR IL'));
                            if (coril && !capturedIds.includes(coril.id)) {
                                capturedIds.push(coril.id);
                            }
                        }
                        // FORCE ADD FERRIM IF UNTOUCHED
                        if (mode === 'register' && confirmedFaction === 3) {
                            const ferrim = allHeroes.find((h: any) => h.name.toUpperCase().includes('FERRIM'));
                            if (ferrim && !capturedIds.includes(ferrim.id)) {
                                console.log('[Auth] Force adding Ferrim for Untouched faction');
                                capturedIds.push(ferrim.id);
                            }
                        }
                        // FORCE ADD NORIM IF HARMONIZED
                        if (mode === 'register' && confirmedFaction === 1) {
                            const norim = allHeroes.find((h: any) => h.name.toUpperCase().includes('NORIM'));
                            if (norim && !capturedIds.includes(norim.id)) {
                                console.log('[Auth] Force adding Norim for Harmonized faction');
                                capturedIds.push(norim.id);
                            }
                            // FORCE ADD TERRIK IF HARMONIZED
                            const terrik = allHeroes.find((h: any) => h.name.toUpperCase().includes('TERRIK'));
                            if (terrik && !capturedIds.includes(terrik.id)) {
                                capturedIds.push(terrik.id);
                            }
                        }
                    }

                    if (capturedIds.length > 0) {
                        // Overwrite local storage with fresh data from server to avoid stale heroes from previous sessions
                        const finalIds = capturedIds;
                        localStorage.setItem('capturedHeroes', JSON.stringify(finalIds));
                        localStorage.setItem('capturedHeroes_p_1', JSON.stringify(finalIds));
                        console.log('[Auth] Saved heroes to localStorage:', finalIds);
                    } else {
                        console.warn('[Auth] No captured IDs to save.');
                        // Clear if server returns empty
                        localStorage.removeItem('capturedHeroes');
                        localStorage.removeItem('capturedHeroes_p_1');
                    }
                } catch (e) {
                    console.error('[Auth] Failed to save heroes', e);
                }
            } else {
                console.warn('[Auth] No heroes in user data response');
            }
            })()
        } catch (e) {
            console.error('[Auth] Network error:', e)
            setError(t('error.registrationFailed', language))
        } finally {
            submitLockRef.current = false
            if (isMountedRef.current) setIsSubmitting(false)
        }
    }

    // Early Alpha Warning Banner Component
    const WarningBanner = () => (
        <div className="w-full bg-gradient-to-r from-amber-900/80 via-orange-800/80 to-amber-900/80 border-b border-amber-500/50 py-2 px-4 text-center backdrop-blur-sm">
            <span className="text-amber-200 text-xs font-medium tracking-wide">
                ⚠️ {t('ui.alphaWarning', language)}
            </span>
        </div>
    )

    // Magical Particles for confirmed faction
    const MagicalParticles = ({ color }: { color: string }) => (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Ambient Background Glow */}
            <div className="absolute inset-0 opacity-30 animate-pulse"
                style={{ background: `radial-gradient(circle at center, ${color} 0%, transparent 70%)` }} />

            {/* Floating Particles */}
            {[...Array(20)].map((_, i) => (
                <div
                    key={i}
                    className="absolute rounded-full animate-[float_3s_ease-in-out_infinite]"
                    style={{
                        width: Math.random() > 0.5 ? '4px' : '2px',
                        height: Math.random() > 0.5 ? '4px' : '2px',
                        background: color,
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        animationDuration: `${2 + Math.random() * 3}s`,
                        animationDelay: `${Math.random() * 2}s`,
                        opacity: 0.6 + Math.random() * 0.4,
                        boxShadow: `0 0 ${5 + Math.random() * 5}px ${color}`,
                    }}
                />
            ))}

            {/* Rising Sparkles */}
            {[...Array(8)].map((_, i) => (
                <div
                    key={`sparkle-${i}`}
                    className="absolute w-1 h-1 bg-white rounded-full animate-[sparkleRise_2s_linear_infinite]"
                    style={{
                        left: `${10 + Math.random() * 80}%`,
                        bottom: '0%',
                        animationDelay: `${Math.random() * 2}s`,
                        boxShadow: '0 0 4px white',
                    }}
                />
            ))}
        </div>
    )

    // Handle proceed to faction selection
    const handleProceedToFaction = () => {
        if ((username || '').trim().length < 2) { setError(t('error.nameLength', language)); return }
        if ((password || '').length < 6) { setError(t('error.passwordLength', language)); return }
        const normalizedOpal = opal.trim()
        if (showOpal && normalizedOpal) {
            const ok = /^[0-9]{8}$/.test(normalizedOpal)
            if (!ok) { setError(t('error.opalFormat', language)); return }
        }
        setError(null)
        setStep('faction')
    }

    // Handle faction confirmation
    const handleConfirmFaction = (factionId: number) => {
        setConfirmedFaction(factionId)
        setSelectedFaction(0) // Collapse the expanded view
    }

    // Handle back to auth
    const handleBackToAuth = () => {
        setStep('auth')
        setSelectedFaction(0)
        setConfirmedFaction(0)
    }

    // CSS for magical animations
    const magicStyles = `
        @keyframes float {
            0%, 100% { transform: translateY(0px) scale(1); opacity: 0.6; }
            50% { transform: translateY(-10px) scale(1.2); opacity: 1; }
        }
        @keyframes sparkleRise {
            0% { transform: translateY(0) scale(0.5); opacity: 0; }
            20% { opacity: 1; }
            100% { transform: translateY(-100px) scale(0); opacity: 0; }
        }
        @keyframes magicGlow {
            0%, 100% { box-shadow: 0 0 20px rgba(34, 197, 94, 0.4), inset 0 0 20px rgba(34, 197, 94, 0.2); border-color: rgba(34, 197, 94, 0.6); }
            50% { box-shadow: 0 0 40px rgba(34, 197, 94, 0.7), inset 0 0 40px rgba(34, 197, 94, 0.4); border-color: rgba(34, 197, 94, 1); }
        }
    `

    const menuBg = useAsset('ui.menu.bg');

    return (
        <div ref={rootRef} className="min-h-screen w-full flex flex-col items-center justify-center" style={{ backgroundImage: `url(${menuBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <style>{magicStyles}</style>

            {/* AUTH STEP - Login/Register Form */}
            {step === 'auth' && (
                <div className="w-full flex flex-col items-center">
                    <div className="relative w-full max-w-md">
                        {/* Peeking Trifle */}
                        <div 
                            className="absolute top-[15%] left-0 transition-all duration-500 pointer-events-none z-0 origin-bottom-right"
                            style={{
                                transform: (isOpalFocused || opal.length > 0) ? 'translateX(-110px) rotate(-40deg)' : 'translateX(-40px) rotate(0) scale(0.9)',
                                opacity: (isOpalFocused || opal.length > 0) ? 1 : 0,
                                transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
                            }}
                        >
                            <img src={triffleAsset} alt="Trifle" className="w-[180px] object-contain drop-shadow-2xl" />
                        </div>

                        <div className="w-full bg-slate-900/90 border-2 border-indigo-500/50 rounded-xl shadow-2xl backdrop-blur-sm transition-all duration-500 overflow-hidden relative z-10">
                            <WarningBanner />
                        <div className="p-8">
                            <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 mb-6 text-center uppercase tracking-wider" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                                {mode === 'login' ? t('ui.welcomeBack', language) : t('ui.joinKingdom', language)}
                            </h2>
                            <p className="text-slate-400 text-sm text-center mb-6">
                                {mode === 'login'
                                    ? t('ui.switchToRegister', language)
                                    : t('ui.switchToLogin', language)
                                }
                            </p>

                            <div className="flex flex-col gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs uppercase font-bold text-slate-400 ml-1">{t('ui.username', language)}</label>
                                    <input value={username} onChange={e => setUsername(e.target.value)} className="w-full px-4 py-3 rounded-lg bg-black/40 text-white border border-slate-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all outline-none placeholder:text-slate-600" placeholder={t('ui.enterName', language)} />
                                </div>

                                <div className="space-y-1">
                                    <label className="block text-slate-400 text-xs font-bold mb-2 uppercase tracking-widest">{t('ui.password', language)}</label>
                                    <input value={password} onChange={e => setPassword(e.target.value)} type="password" className="w-full px-4 py-3 rounded-lg bg-black/40 text-white border border-slate-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all outline-none placeholder:text-slate-600" placeholder={t('ui.enterPassword', language)} />
                                </div>

                                {showOpal && (
                                    <div className="space-y-1">
                                        <label className="block text-slate-400 text-xs font-bold mb-2 uppercase tracking-widest">{t('ui.opalstoneId', language)}</label>
                                        <input 
                                            value={opal} 
                                            onChange={e => setOpal(e.target.value)} 
                                            onFocus={() => setIsOpalFocused(true)}
                                            onBlur={() => setIsOpalFocused(false)}
                                            className="w-full px-4 py-3 rounded-lg bg-black/40 text-white border border-slate-600 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all outline-none placeholder:text-slate-600" 
                                            placeholder={t('ui.enterOpalId', language)} 
                                        />
                                    </div>
                                )}
                            </div>

                            {error && (
                                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 animate-[shake_0.4s_ease-in-out] mt-4">
                                    <div className="text-red-400 text-sm font-medium">{error}</div>
                                </div>
                            )}

                            <button
                                onClick={mode === 'login' ? handleSubmit : handleProceedToFaction}
                                className="mt-6 w-full font-bold py-3.5 rounded-xl shadow-lg transition-all transform uppercase tracking-wide text-sm bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white hover:scale-[1.02] active:scale-[0.98] shadow-indigo-900/30"
                            >
                                {mode === 'login' ? t('ui.login', language) : t('ui.continue', language)}
                            </button>

                            <div className="mt-4 text-center">
                                <button
                                    onClick={() => {
                                        setMode(mode === 'login' ? 'register' : 'login');
                                        setError(null);
                                        setSelectedFaction(0);
                                        setConfirmedFaction(0);
                                    }}
                                    className="w-full py-3.5 rounded-xl font-bold uppercase text-sm tracking-wide border-2 border-slate-700 bg-slate-800/50 text-slate-300 hover:bg-slate-800 hover:border-slate-500 hover:text-white hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg"
                                >
                                    {mode === 'login'
                                        ? t('ui.newToKingdom', language)
                                        : t('ui.alreadyHaveAccount', language)}
                                </button>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>
            )}

            {/* FACTION STEP - Faction Selection (Separate Window) */}
            {step === 'faction' && (
                <div className="w-full flex flex-col items-center p-6">
                    <div className="w-full max-w-2xl bg-slate-900/90 border-2 border-indigo-500/50 rounded-xl shadow-2xl backdrop-blur-sm transition-all duration-500 overflow-hidden" onClick={() => { if (selectedFaction) setSelectedFaction(0) }}>
                        <WarningBanner />
                        <div className="p-8">
                            <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 mb-2 text-center uppercase tracking-wider" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                                {t('ui.chooseYourFaction', language)}
                            </h2>
                            <p className="text-slate-400 text-sm text-center mb-6">{t('ui.selectAllegiance', language, { username: username || 'Traveler' })}</p>

                            <div className="animate-[fadeIn_0.3s_ease-out]" onClick={e => e.stopPropagation()}>
                                <div className="flex justify-center items-stretch gap-4 h-64 relative" onMouseLeave={() => setHoveredFaction(null)}>
                                    {[
                                        { id: 1, name: t('ui.faction.harmonized', language), crest: harmonizedCrest, color: 'border-blue-500/50', bg: 'bg-blue-900/20', particleColor: '#3b82f6', heroes: [{ id: 'NORIM', name: 'Norim', img: norimAvatar, card: norimCard }, { id: 'TERRIK', name: 'Terrik', img: terrikAvatar, card: terrikCard }] },
                                        { id: 2, name: t('ui.faction.chaotic', language), crest: chaoticCrest, color: 'border-red-500/50', bg: 'bg-red-900/20', particleColor: '#ef4444', heroes: [{ id: 'EVELION', name: 'Evelion', img: evelionAvatar, card: evelionCard }, { id: 'CORIL', name: 'Coril', img: corilAvatar, card: corilCard }] },
                                        { id: 3, name: t('ui.faction.untouched', language), crest: untouchedCrest, color: 'border-slate-200/50', bg: 'bg-slate-200/10', particleColor: '#e2e8f0', heroes: [{ id: 'ASTRID', name: 'Astrid', img: astridAvatar, card: astridCard }, { id: 'FERRIM', name: 'Ferrim', img: ferrimAvatar, card: ferrimCard }] }
                                    ].map(f => {
                                        const isConfirmed = confirmedFaction === f.id
                                        const isSelected = selectedFaction === f.id
                                        const isHovered = hoveredFaction === f.id
                                        // Allow expansion on hover even when a faction is confirmed
                                        const isExpanded = isSelected || (isHovered && !selectedFaction)
                                        const isHidden = (selectedFaction && !isSelected) || (!selectedFaction && hoveredFaction && !isHovered)

                                        return (
                                            <div
                                                key={f.id}
                                                className={`relative rounded-xl border-2 transition-all duration-500 ease-in-out overflow-hidden cursor-pointer
                                                    ${isExpanded
                                                        ? `flex-[3] ${isConfirmed ? 'border-green-500 bg-green-900/30' : `${f.color} ${f.bg}`} shadow-[0_0_25px_rgba(0,0,0,0.5)]`
                                                        : isConfirmed
                                                            ? 'flex-1 border-green-500 bg-green-900/30 shadow-[0_0_30px_rgba(34,197,94,0.4)]'
                                                            : isHidden
                                                                ? 'flex-[0] opacity-0 w-0 border-0 p-0 m-0 pointer-events-none'
                                                                : `flex-1 border-slate-700 bg-slate-800/50 hover:border-slate-500 hover:bg-slate-800 hover:flex-[1.2]`}
                                                    ${isConfirmed ? '' : isSelected ? 'ring-2 ring-offset-2 ring-offset-slate-900 ring-indigo-500' : ''}
                                                `}
                                                style={isConfirmed ? { animation: 'magicGlow 2s ease-in-out infinite' } : {}}
                                                onMouseEnter={() => !selectedFaction && setHoveredFaction(f.id)}
                                                onClick={() => {
                                                    if (isConfirmed) {
                                                        // Если фракция уже подтверждена, снимаем выбор
                                                        setConfirmedFaction(0)
                                                        setSelectedFaction(0)
                                                    } else if (selectedFaction === f.id) {
                                                        // Если фракция уже развернута (второй клик), подтверждаем её
                                                        setConfirmedFaction(f.id)
                                                        setSelectedFaction(0)
                                                        setHoveredFaction(null)
                                                    } else {
                                                        // Первый клик: только разворачиваем фракцию для предпросмотра
                                                        setSelectedFaction(f.id)
                                                        setHoveredFaction(f.id)
                                                    }
                                                }}
                                            >
                                                {/* Magical Particles for confirmed faction */}
                                                {isConfirmed && <MagicalParticles color={f.particleColor} />}

                                                <div className={`flex flex-col items-center justify-center h-full p-4 relative z-10`}>
                                                    {/* Crest - Show when NOT expanded */}
                                                    {!isExpanded && (
                                                        <div className={`transition-all duration-500 w-24 h-24 relative ${isConfirmed ? 'scale-110' : ''}`}>
                                                            <img src={f.crest} className={`w-full h-full object-contain drop-shadow-lg ${isConfirmed ? 'drop-shadow-[0_0_10px_rgba(34,197,94,0.8)]' : ''}`} />
                                                            <div className={`text-center mt-2 font-bold text-xs uppercase ${isConfirmed ? 'text-green-400' : 'text-slate-400'}`}>
                                                                {f.name}
                                                                {isConfirmed && <span className="block text-[10px] text-green-500 mt-1">✓ {t('ui.selected', language)}</span>}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Expanded Content - Show on hover/selection (even if confirmed) */}
                                                    {isExpanded && (
                                                        <div className="flex-1 flex flex-col items-center justify-center animate-[fadeIn_0.5s_ease-out] w-full">
                                                            <div className="text-3xl font-bold text-white uppercase mb-4 tracking-widest drop-shadow-lg">{f.name}</div>
                                                            <div className="flex gap-6 justify-center items-center">
                                                                {f.heroes.map(h => (
                                                                    <div
                                                                        key={h.name}
                                                                        className="relative group/hero"
                                                                        onMouseEnter={() => setHoveredHero(h)}
                                                                        onMouseLeave={() => setHoveredHero(null)}
                                                                    >
                                                                        <div className="w-20 h-20 rounded-full border-4 border-white/50 overflow-hidden hover:border-white hover:scale-110 transition-all cursor-help shadow-[0_0_15px_rgba(0,0,0,0.5)] bg-black">
                                                                            <img src={h.img} className="w-full h-full object-cover" />
                                                                        </div>
                                                                        <div className="text-center text-sm font-bold mt-2 text-slate-200 shadow-black drop-shadow-md">{t(`hero.${(h as any).id}.name`, language)}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div className="mt-4 text-xs text-indigo-300 animate-pulse">{t('ui.clickAgainToConfirm', language)}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })}

                                    {/* Hero Preview Overlay */}
                                    {hoveredHero && (
                                        <div className="absolute inset-0 z-50 pointer-events-none flex items-center justify-center bg-black/80 backdrop-blur-sm rounded-xl animate-[fadeIn_0.2s_ease-out]">
                                            <div className="flex items-center gap-6 p-6">
                                                <img src={hoveredHero.card} className="h-56 rounded-lg shadow-[0_0_20px_rgba(255,255,255,0.2)]" />
                                                <div className="max-w-xs text-left">
                                                    <h3 className="text-2xl font-bold text-white mb-2">{t(`hero.${(hoveredHero as any).id}.name`, language)}</h3>
                                                    <p className="text-slate-300 text-sm leading-relaxed">{getHeroDescription((hoveredHero as any).id, language)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {error && (
                                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 animate-[shake_0.4s_ease-in-out] mt-4">
                                    <div className="text-red-400 text-sm font-medium">{error}</div>
                                </div>
                            )}

                            <div className="flex gap-4 mt-6">
                                <button
                                    type="button"
                                    onClick={handleBackToAuth}
                                    className="flex-1 font-bold py-3.5 rounded-xl shadow-lg transition-all transform uppercase tracking-wide text-sm border-2 border-slate-700 bg-slate-800/50 text-slate-300 hover:bg-slate-800 hover:border-slate-500 hover:text-white hover:scale-[1.02] active:scale-[0.98]"
                                >
                                    ← {t('ui.back', language)}
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        if (isSubmitting) return
                                        console.log('[Auth] Begin Journey clicked, confirmedFaction:', confirmedFaction)
                                        if (!confirmedFaction) {
                                            setError(t('error.chooseFaction', language));
                                            return
                                        }
                                        handleSubmit()
                                    }}
                                    disabled={!confirmedFaction || isSubmitting}
                                    className={`flex-[2] font-bold py-3.5 rounded-xl shadow-lg transition-all transform uppercase tracking-wide text-sm
                                        ${(!confirmedFaction || isSubmitting)
                                            ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white hover:scale-[1.02] active:scale-[0.98] shadow-green-900/30'}
                                    `}
                                >
                                    {isSubmitting ? t('ui.loadingAuth', language) : `${t('ui.beginJourney', language)} →`}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isSubmitting && (
                <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center">
                    <div className="rounded-2xl border border-white/10 bg-slate-950/90 px-6 py-5 shadow-2xl flex items-center gap-4">
                        <div className="h-8 w-8 rounded-full border-4 border-slate-400/30 border-t-emerald-400 animate-spin" />
                        <div className="text-left">
                            <div className="text-white font-semibold tracking-wide">{t('ui.loadingAuth', language)}</div>
                            <div className="text-slate-400 text-xs">{t('ui.beginJourney', language)}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AuthScreen;
